import React, { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import axios from 'axios';
import notify, { MESSAGES } from '../../../utils/notify';
import { confirmAction } from '../../../utils/confirm';

import { api } from '../../../config';
import logger from '../../../utils/logger';
import ActionButton from '../../../Components/Common/ActionButton';
import { FileField, RequiredNote } from '../../../Components/Form';

const ACCEPTED_EXTENSIONS = ['.xlsx', '.xls'];

const hasAcceptedExtension = fileName =>
  ACCEPTED_EXTENSIONS.some(ext => fileName.toLowerCase().endsWith(ext));

// Instance axios default memasang DUA interceptor response yang sama-sama meng-unwrap
// `response.data` (helpers/api_helper.js dan core/api_helper.js), sehingga envelope
// ApiResponse bisa terbuka nol, satu, atau dua lapis tergantung modul mana yang termuat.
// Ambil kandidat pertama yang benar-benar berisi hasil upload.
const pickUploadResult = response => {
  const candidates = [response?.data?.data, response?.data, response];
  return (
    candidates.find(
      candidate =>
        candidate && typeof candidate === 'object' && 'totalRows' in candidate
    ) ?? {}
  );
};

const UploadNonLPGModal = ({
  isOpen,
  toggle,
  token,
  onUploaded,
  singleAlpha = false,
}) => {
  // Avtur / High Speed Diesel: satu kolom "Alpha" tanpa Dyed/Undyed.
  const alphaLabel = singleAlpha ? 'Alpha' : 'Alpha Dyed/Undyed';
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  // Endpoint membalas 200 walaupun sebagian/seluruh baris gagal, jadi hasil parsial
  // harus ditampilkan sendiri alih-alih mengandalkan blok catch.
  const [rowErrors, setRowErrors] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setFile(null);
      setFileError('');
      setRowErrors([]);
    }
  }, [isOpen]);

  const handleFileChange = event => {
    const selected = event.target.files?.[0] ?? null;

    if (selected && !hasAcceptedExtension(selected.name)) {
      setFile(null);
      setFileError('File must be in .xlsx or .xls format.');
      return;
    }

    setFile(selected);
    setFileError('');
    setRowErrors([]);
  };

  const handleClose = () => {
    if (isUploading) return;
    toggle();
  };

  const handleUpload = async () => {
    if (!file) {
      setFileError('File is required');
      return;
    }

    if (!token) {
      notify.error(MESSAGES.authRequired);
      return;
    }

    const confirmed = await confirmAction({
      tone: 'warning',
      title: `Upload ${singleAlpha ? 'Alpha' : 'Alpha Dyed/Undyed'} file?`,
      message:
        'Rows already in the table may be updated or replaced by the contents of this file.',
      subject: file.name,
      confirmLabel: 'Upload file',
      cancelLabel: 'Review again',
    });
    if (!confirmed) return;

    setIsUploading(true);
    setRowErrors([]);

    try {
      const formData = new FormData();
      formData.append('File', file);

      // Parcel non-LPG tidak punya baris cargo ratio, jadi alpha dyed/undyed disimpan
      // di QuantityInformationDetail (Straits) dan QuantityIncotermLocationDetail (per
      // lokasi) - keduanya diisi lewat endpoint upload parcel yang sama dengan freight.
      const response = await axios.post(
        `${api.API_URL}/quantity-incoterm-location-detail/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = pickUploadResult(response);
      const total = result.totalRows ?? 0;
      const success = result.successCount ?? 0;
      const failed = result.failedCount ?? 0;

      if (failed > 0) {
        setRowErrors(result.errors ?? []);
        notify.warning(
          `${success} of ${total} rows updated, ${failed} failed.`
        );
        // Modal sengaja dibiarkan terbuka supaya daftar baris yang gagal bisa dibaca.
        if (success > 0) onUploaded?.();
      } else {
        notify.success(`${success} of ${total} rows updated.`);
        onUploaded?.();
        toggle();
      }
    } catch (err) {
      const status = err?.response?.status;
      logger.error(
        `Error uploading ${alphaLabel} file:`,
        status ? `HTTP ${status}` : (err?.message ?? String(err))
      );
      notify.apiError(err, `upload ${alphaLabel} file`);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose} className='border-0 pb-0'>
        <span className='fw-semibold'>
          {singleAlpha ? 'Upload Alpha' : 'Upload Alpha Dyed / Undyed'}
        </span>
        <span className='d-block text-muted small fs-12 mt-1 '>
          {singleAlpha
            ? 'Upload the Alpha parcel file that has already been filled in.'
            : 'Upload the Alpha Dyed/Undyed parcel file that has already been filled in.'}
        </span>
      </ModalHeader>

      <ModalBody>
        <hr className='mt-0' />
        <RequiredNote />
        <FileField
          label='File'
          name='nonLpgFile'
          required
          accept={ACCEPTED_EXTENSIONS.join(',')}
          hint='Excel file (.xlsx or .xls)'
          error={fileError}
          mode={isUploading ? 'disabled' : 'editable'}
          onChange={handleFileChange}
        />

        {rowErrors.length > 0 && (
          <div className='mt-3'>
            <div className='fw-semibold text-danger mb-1'>
              Rows that failed ({rowErrors.length})
            </div>
            <div
              className='border rounded p-2 small'
              style={{ maxHeight: 180, overflowY: 'auto' }}
            >
              {rowErrors.map(err => (
                <div key={err.rowNumber ?? `${err.message}`}>
                  Row {err.rowNumber}: {err.message}
                </div>
              ))}
            </div>
          </div>
        )}
      </ModalBody>

      <ModalFooter className='border-0'>
        <ActionButton
          variant='secondary'
          icon='ri-close-line'
          onClick={handleClose}
          disabled={isUploading}
        >
          Close
        </ActionButton>
        <ActionButton
          variant='primary'
          icon='ri-upload-2-line'
          onClick={handleUpload}
          loading={isUploading}
        >
          Upload File
        </ActionButton>
      </ModalFooter>
    </Modal>
  );
};

export default UploadNonLPGModal;
